import { isBuildToolResultFailure } from './prototyperBuildPhases';

/**
 * What a turn that ended with the founder's Stop left behind (test of 19 Sep: Stop at the end of a
 * build).
 *
 * - `build-passed`: this turn's build already passed when Stop landed. The version is finished (its
 *   verified snapshot is saved from the build), so the turn is treated as succeeded. Before, the
 *   Stop counted as a failed transport: the build passed and the server verified the version, but
 *   the page kept the previous version as the one on screen, so the panel title, Publish and Fix
 *   with AI all acted on the previous version.
 * - `unfinished`: stopped before its build passed. The version stays a draft that Continue can
 *   finish; the page shows it as "Didn't finish" and puts the last finished version back on screen.
 *   Before, the panel stayed on "Building and checking your prototype..." and the pill offered no
 *   Continue until a reload.
 * - `not-stopped`: the turn did not end with Stop (it finished, or the connection failed).
 */
export type StoppedTurnOutcome = 'not-stopped' | 'build-passed' | 'unfinished';

export function stoppedTurnOutcome(args: {
  isAbort: boolean;
  isError: boolean;
  isDisconnect: boolean;
  /** `buildStatus === 'success'` with no failed build step after it. */
  buildOk: boolean;
  /** This turn's steps, to require a build that completed in this turn. */
  toolSteps: readonly { toolName: string; isComplete: boolean; result?: string }[];
}): StoppedTurnOutcome {
  const { isAbort, isError, isDisconnect, buildOk, toolSteps } = args;
  if (!isAbort || isError || isDisconnect) return 'not-stopped';
  return buildOk && lastToolStepBuildPassed(toolSteps) ? 'build-passed' : 'unfinished';
}

/** The turn's last completed build step exists and passed. */
export function lastToolStepBuildPassed(
  toolSteps: readonly { toolName: string; isComplete: boolean; result?: string }[]
): boolean {
  const lastBuild = [...toolSteps].reverse().find((s) => s.toolName === 'build' && s.isComplete);
  return Boolean(lastBuild?.result) && !isBuildToolResultFailure(lastBuild!.result!);
}
